import { useState } from "react"
import Slugify from "slugify"
import { graphql, Link, withPrefix } from "gatsby"
import { Card, TabCarousel } from "@fastly/consistently"

import mapPageComponents from "@utils/mapPageComponents"
import { reduceMicrocopySet } from "@utils/reduceMicrocopy"
import RichText from "@components/rich-text/renderer"
import Hero from "@components/hero"
import { FastlyImage } from "@components/FastlyImage"

import {
  page as page_cn,
  container,
  options,
  filters,
  filter,
  filterInfo,
  companies as companies_cn,
  carousel,
} from "./customers.module.css"
import { textButton, arrowRight } from "@components/button.module.css"

import "../../fragments/Page"
import { MikanSpan } from "@components/Mikan"

const Customers = ({ data: { page, companies } }) => {
  const { hero, blockCarousel } = mapPageComponents(page.content)
  const [industryFilter, setIndustryFilter] = useState("")
  const [regionFilter, setRegionFilter] = useState("")
  const [productFilter, setProductFilter] = useState("")

  const microcopySet = page.content.find((page) => page.contentful_id === "5uiswFZPrhCvzg8VRhtJOx")

  const labels = reduceMicrocopySet(microcopySet)

  const filteredCompanies = companies.edges
    .filter(
      (company) => industryFilter === "" || (company.node.industry && company.node.industry.includes(industryFilter))
    )
    .filter((company) => regionFilter === "" || (company.node.region && company.node.region.includes(regionFilter)))
    .filter((company) => productFilter === "" || (company.node.product && company.node.product.includes(productFilter)))

  const localizedCarousel = blockCarousel.filter((company) => !company.excludeFromLocale)

  const tabContent = localizedCarousel.map((company) => {
    return company.section_content
      .filter((item) => item.__typename === "ContentfulText")
      .map((text) => (
        <div key={text.contentful_id}>
          <RichText body={text.rtContent} mikan={text.mikan} />
        </div>
      ))
  })

  const navContent = localizedCarousel.map((company) => {
    return company.section_content
      .filter((item) => item.__typename === "ContentfulImage")
      .map((img, i) => (
        <FastlyImage
          data-index={i}
          key={i}
          contentKey={i}
          src={img.content.file.url}
          alt={img.content.title}
          params="?width=118&fit=fill"
        />
      ))
  })

  const showingOfLabel = (labels["customers.showingOf"] || "")
    .replace("{sub}", filteredCompanies.length)
    .replace("{total}", companies.edges.length)

  const areAllCustomersListed = companies.edges.length === filteredCompanies.length

  return (
    <main className={page_cn}>
      <Hero type="solid">
        <RichText body={hero.rtContent} mikan={hero.mikan} />
      </Hero>

      <div className={carousel}>
        <TabCarousel navContent={navContent}>{tabContent}</TabCarousel>
      </div>

      <div className={container}>
        <div className={options}>
          <div className={filters}>
            <div className={filter}>
              <label htmlFor="industry-filter">{labels["customers.filterIndustry"]}</label>
              <select
                id="industry-filter"
                value={industryFilter}
                onChange={(evt) => setIndustryFilter(evt.target.value)}
              >
                <option value="">{labels["customers.all"]}</option>
                {companies.industries.map((industry) => (
                  <option key={industry.fieldValue} value={industry.fieldValue}>
                    {labels[`customers.${Slugify(industry.fieldValue)}`]}
                  </option>
                ))}
              </select>
            </div>

            <div className={filter}>
              <label htmlFor="region-filter">{labels["customers.filterRegion"]}</label>
              <select id="region-filter" value={regionFilter} onChange={(evt) => setRegionFilter(evt.target.value)}>
                <option value="">{labels["customers.all"]}</option>
                {companies.regions.map((region) => (
                  <option key={region.fieldValue} value={region.fieldValue}>
                    {labels[`customers.${Slugify(region.fieldValue)}`]}
                  </option>
                ))}
              </select>
            </div>

            <div className={filter}>
              <label htmlFor="product-filter">{labels["customers.filterProduct"]}</label>
              <select id="product-filter" value={productFilter} onChange={(evt) => setProductFilter(evt.target.value)}>
                <option value="">{labels["customers.all"]}</option>
                {companies.products.map((product) => (
                  <option key={product.fieldValue} value={product.fieldValue}>
                    {labels[`customers.${Slugify(product.fieldValue)}`]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={filterInfo}>
            {showingOfLabel}
            {!areAllCustomersListed && (
              <>
                {" |"}
                <button
                  className={textButton}
                  onClick={() => {
                    setIndustryFilter("")
                    setRegionFilter("")
                    setProductFilter("")
                  }}
                >
                  {labels["customers.showAll"]}
                </button>
              </>
            )}
          </div>
        </div>

        <div className={companies_cn}>
          {filteredCompanies.map((company) => {
            const { contentful_id, caseStudyTeaser, name, caseStudy, logo } = company.node
            const summary = caseStudyTeaser ? caseStudyTeaser.caseStudyTeaser : caseStudy.socialDescription

            return (
              <Card
                key={contentful_id}
                dataLinkId={contentful_id}
                link={withPrefix(caseStudy.url)}
                header={name}
                image={logo && logo.file.url}
                type="paddedImage"
                summary={<MikanSpan justify text={summary} />}
                footer={
                  <Link to={caseStudy.url} className={arrowRight}>
                    <MikanSpan text={labels["customers.readMore"]} />
                  </Link>
                }
              />
            )
          })}
        </div>
      </div>
    </main>
  )
}

export default Customers

export const query = graphql`
  query ($contentful_id: String!) {
    page: contentfulPage(contentful_id: { eq: $contentful_id }) {
      ...Page
    }
    companies: allContentfulCompany(
      filter: {
        caseStudyTeaser: { caseStudyTeaser: { ne: null } }
        caseStudy: { id: { ne: null }, excludeFromLocale: { ne: true } }
        excludeFromCustomers: { ne: true }
      }
      sort: { caseStudy: { createdAt: DESC } }
    ) {
      edges {
        node {
          contentful_id
          name
          industry
          region
          product
          logo {
            file {
              url
            }
          }
          caseStudyTeaser {
            caseStudyTeaser
          }
          caseStudy {
            url
            socialDescription
          }
        }
      }
      regions: group(field: { region: SELECT }) {
        fieldValue
      }
      industries: group(field: { industry: SELECT }) {
        fieldValue
      }
      products: group(field: { product: SELECT }) {
        fieldValue
      }
    }
  }
`

export { Head } from "@components/Head/Head"
